import { Fragment, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { FiTrash } from "react-icons/fi";
import { EntityContext } from '../../context/EntityContext';
import { getFormatedFileSize } from '../../helper/fileSizeFormatter';
import { RefUploader } from '../../pages/plans/components/files/refUploader';
import { FileIcon } from '../FileIcon';
import { S3DeleteFile, S3ListFiles } from '../../helper/s3ClientHandler';
import {TfiClose} from "react-icons/tfi";

export const AddRefToPlan = () => {
   const cancelButtonRef = useRef(null);
     const { openPlanRefModal, setOpenPlanRefModal, currentEntity} = useContext(EntityContext);
     const [uploadedFiles, setUploadedFiles] = useState([]);
     const [loading, setLoading] = useState(false);
   
     const navigate = useNavigate();
   
     const addFilesToUploadedFiles = (files) => {
       //name is now under file.name
       const newFiles = [...uploadedFiles, ...files];
       const filteredFiles = newFiles.filter((file, index, self) =>
         index === self.findIndex((f) => f.name === file.name)
       );
       setUploadedFiles(filteredFiles);
     }
   
     const removeFileFromUploadedFiles = (fileKey) => {
       const newFiles = uploadedFiles.filter((file) => file.key !== fileKey);
       setUploadedFiles(newFiles);
     }
   
     const onFilesUploaded = async ({files}) => {
       addFilesToUploadedFiles(files);
     }
   
     const onFinish = async () => {
            setOpenPlanRefModal(false);
            navigate(`/viewer/${currentEntity?.id}`);
            setLoading(false);
            return true;
     }
   
     const onPlanCancel = async () => {
       setLoading(true);
       setUploadedFiles([]);
       setOpenPlanRefModal(false);
       setLoading(false);
     }
   
     const handleDeleteFile = async (fileKey) => {
       await S3DeleteFile(fileKey);
       removeFileFromUploadedFiles(fileKey);
     }

     useEffect(() => {
        if (!openPlanRefModal) {
            return;
        }
        //get the files in the directory
        const getFiles = async () => {
            const files = await S3ListFiles(`${currentEntity?.createdByCompany}/plans/${currentEntity?.id}`);
            if (!files) {
                return;
            }
            const filteredFiles = files.filter((file) => !file.key.endsWith('master.gml') && !file.key.endsWith('master.geojson'));
            if (filteredFiles.length === 0) {
                return;
            }
            const newFiles = filteredFiles.map((file) => {
                return {
                    key: file.key,
                    name: file.key.split('/').pop(),
                    size: file.size,
                    type: file.key.split('.').pop(),
                }
            });

            setUploadedFiles(newFiles);
        }
        getFiles();
     }, [currentEntity?.createdByCompany, currentEntity?.id, openPlanRefModal]);
   
     return (
        <Transition.Root show={openPlanRefModal} as={Fragment}>
            <Dialog
            className="relative z-10"
            initialFocus={cancelButtonRef}
            onClose={onPlanCancel}
            >
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>
    
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex h-full items-center justify-center sm:px-4 sm:pb-4 text-center pt-[65px] sm:px-4 lg:p-10 max-w-5xl mx-auto">
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <Dialog.Panel className='flex flex-col max-h-full sm:max-h-max h-full rounded-lg bg-white text-left shadow-xl transition-all w-full relative'>
                    <button
                        type='button'
                        className='absolute z-40 lg: lg:-top-6 lg:-right-6 top-2 right-2'
                        onClick={onPlanCancel}
                        disabled={loading}
                        ref={cancelButtonRef}
                    >
                        <TfiClose className={"lg:size-16 border-gray-500/80 border-2 bg-white hover:bg-gray-300 lg:p-4 size-12 p-2 lg:rounded-full rounded-md"}/>
                    </button>
    
    
                    <div className='block md:flex w-full md:overflow-hidden'>
                            <div className=' rounded-tl-lg rounded-bl-lg py-8 px-8 md:flex w-full md:w-2/5 bg-gray-900 flex-col'>
                                <div>
                                    <div className='flex items-center  md:mb-8 gap-2'>
                                        <Dialog.Title
                                            as='h3'
                                            className='font-bold text-2xl leading-6 text-white'>
                                            Referenzen zum Plan hinzufügen
                                        </Dialog.Title>
                                    </div>
                            </div>
        
                            <div className={'h-auto md:h-[450px] '}>
                                <div className='h-full w-full pt-4 pb-8 '>
                                    <div className='relative flex flex-col items-start pl-[0px] justify-between text-white/70 h-full'>
                                    </div>
                                </div>
                            </div>
                            </div>
                            <div className={'md:w-3/5 flex bg-white flex-col w-full'}>
                                <div className='px-8 pt-8 flex flex-col flex-1 overflow-hidden'>
                                    <RefUploader loading={loading} setLoading={setLoading} uploadedFiles={uploadedFiles} onFilesUploaded={onFilesUploaded} planPath={`${currentEntity?.createdByCompany}/plans/${currentEntity?.id}`}/>

                                    <div className="mt-6 overflow-y-auto pr-2">
                                    {uploadedFiles.length > 0 && (
                                        <ul className="list-disc flex flex-col list-inside gap-2 my-1">
                                            {uploadedFiles.map((file, index) => (
                                                <li key={`gml-${index}`} className="flex items-center justify-between rounded-lg  text-sm leading-6 bg-gray-200 py-2 px-2">
                                                <FileIcon fileExtension={file.name.split('.').pop()} />
                                                <div className='flex gap-2 w-full truncate ml-2'>
                                                    <div className='truncate flex-1'>
                                                    <h3 className=' truncate'>{file.name}</h3>
                                                    <div className='flex gap-4'>
                                                        <p className='text-gray-600/60 text-xs '>{file.type} Datei</p>
                                                        <p className='text-gray-600/60 text-xs'>{getFormatedFileSize(+file.size)}</p>
                                                    </div>
                                                    </div>
                                                </div>
                                                <button onClick={() => handleDeleteFile(file.key)}
                                                        className="cursor-pointer mr-2 ml-4 flex"
                                                >
                                                    <FiTrash  className="h-[18px] w-[18px] hover:text-red flex-none text-gray-900"  aria-hidden="true"/>
                                                </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    </div>
                            </div>
                            <div className="p-8 sm:flex-row-reverse flex w-full flex-col-reverse gap-4">
                                <button
                                    type="button"
                                    disabled={loading}
                                    className={`${loading ? 'cursor-progress' : ''} text-center truncate items-center rounded-md bg-white border-gray-900 border-2 disabled:text-gray-600/40 disabled:border-gray-500 px-4 py-3 text-sm font-semibold text-gray-900 `}
                                    onClick={onFinish}
                                >
                                Upload abschließen
                                </button>
                            </div>
                        </div>
                    </div>
                    </Dialog.Panel>
    
                </Transition.Child>
                </div>
            </div>
            </Dialog>
        </Transition.Root>
     );
}
